import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UgmWeatherDataService } from '@6grain/masofa-monolith-api-client/weather';
import { WeatherIndicatorManager } from './weather-indicator.manager';
import { DailyDataResult } from './shared-types';

/**
 * Parameters for UGM data loading
 */
export interface UgmLoadParams {
  latitude: number;
  longitude: number;
  days?: number; // Default 7 days
}


/**
 * Manager for UGM weather data operations
 * Handles daily data loading for the last N days
 */
export class UgmDataManager {
  constructor(
    private ugmWeatherDataService: UgmWeatherDataService,
    private indicatorManager: WeatherIndicatorManager
  ) {}

  /**
   * Load UGM daily data for the last N days
   */
  loadDailyData(params: UgmLoadParams): Observable<DailyDataResult> {
    const days = params.days || 7;
    const today = new Date();
    const dates: Date[] = [];
    const requests: Observable<any>[] = [];

    // Create requests for the last N days
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      dates.push(date);

      const request = this.ugmWeatherDataService.weatherUgmWeatherDataGetByCoordinatesAndDatePost({
        latitude: params.latitude,
        longitude: params.longitude,
        inputDate: date.toISOString().split('T')[0]
      }).pipe(
        catchError(error => {
          console.error(`Error loading UGM data for ${date.toISOString()}:`, error);
          return of(null);
        })
      );

      requests.push(request);
    }

    return forkJoin(requests).pipe(
      map(results => ({
        data: results.filter(r => r !== null),
        dates
      }))
    );
  }

  /**
   * Generate labels for daily data
   */
  generateDailyLabels(dates: Date[], locale: string): string[] {
    return dates.map(date =>
      date.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric'
      })
    );
  }

  /**
   * Extract values from daily data
   */
  extractDailyValues(
    data: any[],
    dates: Date[],
    indicator: string
  ): (number | null)[] {
    const values: (number | null)[] = [];

    dates.forEach(date => {
      const dateStr = date.toISOString().split('T')[0];
      const dayData = data.find(d => {
        const itemDate = new Date(d.date || d.observationDate || d.forecastDate);
        return itemDate.toISOString().split('T')[0] === dateStr;
      });

      if (dayData) {
        values.push(this.indicatorManager.extractValue(dayData, indicator, 'ugm'));
      } else {
        values.push(null);
      }
    });

    return values;
  }

  /**
   * Process raw data into chart-ready format
   */
  processData(
    data: any[],
    dates: Date[],
    indicator: string,
    locale: string
  ): { labels: string[]; values: (number | null)[] } {
    return {
      labels: this.generateDailyLabels(dates, locale),
      values: this.extractDailyValues(data, dates, indicator)
    };
  }
}
